import React, { useEffect, useRef } from 'react';

import type { FlowEngine } from '../../services/flows';
import type { Step } from '../../services/flows/types';
import { VariableResolver } from '../../services/flows/variableResolver';
import { logger } from '../../utils/logger';

// Create component logger for SystemStep
const systemStepLogger = logger.withComponent('SystemStep');

interface SystemStepProps {
  step: Step;
  engine: FlowEngine;
  onMessage: (message: any) => void;
}

const SystemStep: React.FC<SystemStepProps> = ({ step, engine, onMessage }) => {
  // Track whether this step has already sent its message to prevent infinite loops
  const messageSentRef = useRef(false);

  // When this step becomes active, emit its content as a message to the parent.
  // This makes the message persistent in the main chat panel, even after this
  // ephemeral step component is destroyed by the flow advancing.
  useEffect(() => {
    const timestamp = performance.now().toFixed(2);
    systemStepLogger.info(`[${timestamp}ms][SystemStep][effect-fired]`, {
      stepId: step.id,
      hasContent: !!step.content,
      hasImage: !!step.image,
      messageSent: messageSentRef.current,
    });

    if (step.content && !messageSentRef.current) {
      const flowState = engine.getState();
      const variables = flowState.variables;

      // Translate content first, then resolve any variables
      const translatedContent = __(step.content);
      const resolvedContent = VariableResolver.interpolate(translatedContent, variables);
      const resolvedImageUrl = step.image
        ? VariableResolver.interpolate(step.image, variables)
        : undefined;

      const message = {
        from: 'bot',
        text: resolvedContent,
        imageUrl: resolvedImageUrl,
      };

      systemStepLogger.info(`[${timestamp}ms][SystemStep][calling-onMessage]`, {
        stepId: step.id,
        contentLength: resolvedContent.length,
        hasImage: !!resolvedImageUrl,
      });
      
      onMessage(message);
      messageSentRef.current = true;
    }
  }, [step.id, step.content, step.image, engine, onMessage]);

  // This component renders no HTML itself.
  // Its sole purpose is to emit its message content to the parent ChatbotWidget.
  return null;
};

export default SystemStep;